import { useState, useEffect, useRef, useCallback } from 'react'
import { suggestLawNames } from '@/lib/api'

interface Props {
  onSearch: (query: string) => void
  isLoading?: boolean
}

export default function SearchBar({ onSearch, isLoading }: Props) {
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [history, setHistory] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('klaw_history') ?? '[]')
    } catch {
      return []
    }
  })
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // 자동완성
  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.length < 1) { setSuggestions([]); return }
    try {
      const list = await suggestLawNames(q)
      setSuggestions(list.slice(0, 8))
    } catch {
      setSuggestions([])
    }
  }, [])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSuggestions(query), 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query, fetchSuggestions])

  const handleSubmit = (q: string) => {
    if (!q.trim()) return
    const newHistory = [q, ...history.filter((h) => h !== q)].slice(0, 10)
    setHistory(newHistory)
    localStorage.setItem('klaw_history', JSON.stringify(newHistory))
    setShowSuggestions(false)
    onSearch(q)
  }

  const clearHistory = () => {
    setHistory([])
    localStorage.removeItem('klaw_history')
  }

  return (
    <div className="relative w-full">
      <form
        onSubmit={(e) => { e.preventDefault(); handleSubmit(query) }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            placeholder="법령명, 판례, 조문 내용 검색... (예: 근로기준법, 화관법)"
            className="w-full pl-12 pr-4 py-3 text-sm border border-gray-200 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-law-500 focus:border-transparent
                       bg-white shadow-sm placeholder:text-gray-400"
          />

          {/* 자동완성 / 히스토리 드롭다운 */}
          {showSuggestions && (suggestions.length > 0 || history.length > 0) && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100
                            rounded-xl shadow-lg z-50 overflow-hidden">
              {/* 자동완성 */}
              {suggestions.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 bg-gray-50">
                    법령명 자동완성
                  </div>
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onMouseDown={() => { setQuery(s); handleSubmit(s) }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700
                                 hover:bg-law-50 hover:text-law-700 flex items-center gap-2"
                    >
                      <span className="text-gray-300">⚖️</span>
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* 검색 히스토리 */}
              {query.length === 0 && history.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 bg-gray-50
                                  flex justify-between items-center">
                    <span>최근 검색</span>
                    <button
                      type="button"
                      onMouseDown={clearHistory}
                      className="text-xs text-gray-400 hover:text-red-500"
                    >
                      전체 삭제
                    </button>
                  </div>
                  {history.slice(0, 6).map((h) => (
                    <button
                      key={h}
                      type="button"
                      onMouseDown={() => { setQuery(h); handleSubmit(h) }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-600
                                 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <span className="text-gray-300 text-xs">🕐</span>
                      {h}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading || !query.trim()}
          className="px-6 py-3 bg-law-600 text-white text-sm font-semibold rounded-xl
                     hover:bg-law-700 active:bg-law-800
                     disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors shadow-sm"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              검색 중
            </span>
          ) : '검색'}
        </button>
      </form>
    </div>
  )
}
